import { ParsedDataTypeSpecifier } from "../../HistoricalData.js";
import { ManufacturerName, SensorRawDataFormat, SensorType, SproutSensorID } from "../../shared.js";

export type SensorParameters = Record<string, number | string>;
/**
 * Defines all the information that must be passed to a conversion function.
 *
 * This information is used to generate a parsed value from a raw value or vice versa.
 * It includes the sensor ID, data type, raw value, and any conversion parameters
 * needed for the specific sensor.
 */
type SproutConversionFunctionInfo = {
    sensorID: SproutSensorID;
    dataType: ParsedDataTypeSpecifier;
    rawValue: number;
    preset?: string;
    // should add boolean as an option in the future, not needed right now.
    sensorParameters: SensorParameters;
};

/**
 * A function that validates a sensor parameter value.
 *
 * @param value The value to validate
 * @returns A tuple where:
 *   - First element is a boolean indicating if the value is valid (true) or invalid (false)
 *   - Second element is an empty string if valid, or an error message if invalid
 */
export type SensorParameterValidator<ValueType> = (value: ValueType) => [boolean, string];

export enum SensorParameterValueType {
    STRING = "STRING",
    NUMBER = "NUMBER",
}

/**
 * Defines the configuration for a single sensor parameter.
 *
 * This configuration provides all the information needed to:
 * - Display the parameter in the UI
 * - Validate user input
 *
 * The structure is common whether it's a number or a string, but the "type" property
 * determines which additional properties are required.
 */
export type SensorParameterConfigBase<ValueType extends number | string> = {
    /**
     * The key defining where the parameter is stored on the sensor object.
     */
    key: string;

    type: ValueType extends number
        ? SensorParameterValueType.NUMBER
        : ValueType extends string
          ? SensorParameterValueType.STRING
          : never;
    /**
     * Friendly parameter name provided to users
     */
    name: string;

    /**
     * Parameter description which will show up as a subheading to describe the parameter to the user
     */
    description?: string;

    /**
     * If this parameter is only allowed to have specific discrete values
     * then they should be listed here
     */
    valueOptions?: {
        /**
         * What the value should be set to when this option is selected
         */
        value: ValueType;
        /**
         * Name of the option presented to users
         */
        optionName: string;
        /**
         * Not sure if we will use this, but if we do it will appear under each option to indicate any
         * details needed beyond the name
         */
        optionDescription: string;
    }[];

    /**
     * Value that this parameter should be set to on sensor registration
     * before the user has selected anything
     * @param value
     */
    defaultValue: ValueType;

    /**
     * Should return [true, ""] if the value is valid.
     * If the value is invalid, return [false, "reason"] where reason is
     * a string that contains a translation key for the reason.
     * @param value
     */
    validator: SensorParameterValidator<ValueType>;

    /**
     * The following properties will only be necessary if the value type is numeric
     */
} & (ValueType extends number
    ? {
          /**
           * Set to true if the value for this parameter must be a positive number.
           *
           * - This is purely to inform the UI. This does not enforce any validation on its own
           * - If this is true, you must use a validator that prevents setting negative numbers
           * - This should only be set on numeric parameters.
           *
           * UI implications:
           * This will block the user from using a "-" sign in the numeric input field.
           */
          mustBeNonNegative: boolean;

          /**
           * Set to true if the value for this parameter must be an integer
           *
           * - This is purely to inform the UI. This does not enforce any validation on its own
           * - If this is true, you must use a validator that prevents setting non-integers
           * - This should only be set on numeric parameters.
           *
           * UI implications:
           * This will block the user from using a "." sign in the numeric input field.
           */
          mustBeInteger: boolean;

          /**
           * The category of the sensor parameter unit.
           * This is used to determine the unit that should be used for the sensor parameter.
           *
           * This should only be set on numeric parameters
           */
          parameterUnitCategory: UnitCategory;
      }
    : Record<string, never>);

/**
 * Make sure that type is set correctly on these
 */
export type SensorParameterConfig = SensorParameterConfigBase<number | string>;

/**
 * Type guard to check if a sensor parameter configuration is for a numeric parameter.
 *
 * @param sensorParameter The parameter configuration to check
 * @returns True if the parameter is numeric, false otherwise
 */
export function isNumericSensorParameterConfig(
    sensorParameter: SensorParameterConfig,
): sensorParameter is SensorParameterConfigBase<number> {
    return sensorParameter.type === SensorParameterValueType.NUMBER;
}

/**
 * Type guard to check if a sensor parameter configuration is for a string parameter.
 *
 * @param sensorParameter The parameter configuration to check
 * @returns True if the parameter is a string, false otherwise
 */
export function isStringSensorParameterConfig(
    sensorParameter: SensorParameterConfig,
): sensorParameter is SensorParameterConfigBase<string> {
    return sensorParameter.type === SensorParameterValueType.STRING;
}

/**
 * Raw sensor reading value, typically in voltage (V) or frequency (Hz)
 * Gotta work on the naming
 */
export type RawValue = number;

/**
 * Processed sensor reading value in the appropriate units for the sensor type
 * Gotta work on the naming
 */
export type ParsedValue = number;

/**
 * Function to convert a raw sensor reading to one or more parsed values.
 *
 * @param rawValue The raw sensor reading (normalized to voltage or PWM frequency)
 * @param options Additional information needed for the conversion
 * @returns Array of data points with their data type and parsed value
 */
export type SensorRawDataToParsedDataConversionFunction = (
    rawValue: RawValue,
    options: SproutConversionFunctionInfo,
) => { dataType: ParsedDataTypeSpecifier; value: ParsedValue }[];

/**
 * Function to convert a parsed value back to a raw sensor reading.
 *
 * @param parsedValue The parsed sensor value in appropriate units
 * @param options Additional information needed for the conversion
 * @returns The raw value that would produce the given parsed value
 */
export type SensorParsedDataToRawDataConversionFunction = (
    parsedValue: ParsedValue,
    options: SproutConversionFunctionInfo,
) => RawValue;

export enum VoltageRequirement {
    VOLTS_12 = 12,
    VOLTS_5 = 5,
}
/**
 * Base configuration for a sensor type.
 *
 * This type defines all the information needed to add support for a new sensor.
 * It includes conversion functions, physical characteristics, display information,
 * and configuration parameters.
 */
export type SensorConfigurationBase = {
    /**
     * For third party devices, set this to null
     */
    verdiDeviceSensorConfiguration: {
        /**
         * Version selector to identify what device versions this sensor is supported by
         */
        supportedDeviceVersions: string;
        /**
         * Defines what type of reading comes in at the sensor port (ADC, PWM, etc.)
         */
        rawDataFormat: SensorRawDataFormat;
        /**
         * What voltage is required to power the sensor (12 volts, 5 volts, etc.)
         */
        requiredVoltage: VoltageRequirement;
        /**
         * Function to convert a raw sensor reading (Hz or voltage) to a parsed reading (parsedDataType)
         */
        rawDataToParsedDataConversion: SensorRawDataToParsedDataConversionFunction;
        /**
         * Function to convert a parsed sensor reading (parsed data type) to a raw sensor reading (Hz, or voltage)
         */
        parsedDataToRawDataConversion?: SensorParsedDataToRawDataConversionFunction;

        /**
         * Basic way to select what bitrate we should try to shoot for on this sensor
         */
        precisionImportance: "high" | "medium" | "low";
        /**
         * If true, then as the raw value increases, the parsed value also increases.
         * TODO: rename to `outputIncreasesWithInput` once migration from legacy sensor config format is complete.
         */
        rawAndParsedValuesIncreaseTogether: boolean;
        /**
         * Has to do with power profile of sensor. Block Gen4  can only totalize sensors provided the power drain is low
         * enough. Most small flowmeters cannot be totalized, most large ones can.
         */
        supportsLowPowerTotalization: boolean;
    } | null;

    /**
     * Should be a lookup table of parameters that are needed for interpreting values from the sensor.
     * All parameters should always be filled out and they should start at defaults.
     * No parameters may be left unfilled. Leave as an empty object
     * if no sensor parameters are needed.
     */
    sensorParameterConfigs: Record<string, SensorParameterConfig>;

    parsedDataType: ParsedDataTypeSpecifier;

    /**
     * The measurement uncertainty specified by the manufacturer or determined experimentally by us.
     * Ideally, this should represent the 95%/two-sigma measurement uncertainty.
     */
    measurementUncertainty?: number;

    /**
     * If there is an absolute minimum reading for a sensor that it will never go below, it should be listed here
     * Otherwise, leave it blank
     */
    minimumReading?: number;

    /**
     * If there is an absolute maximum reading for a sensor that it should never exceed, it should be listed here.
     * Otherwise, leave it blank
     */
    maximumReading?: number;

    /**
     * If true, then previous value is assumed to be correct until a new value is found.
     * Commonly set to true on flow meters and pressure transducers.
     */
    useZeroOrderHoldInterpolation?: boolean;

    /**
     * The full manufacturer name.
     */
    manufacturerName: ManufacturerName;

    /**
     * The model name as written by the manufacturer on their website or on the label for the device.
     * Will often be just a bunch of letters and numbers.
     * It should NOT include the manufacturer name
     */
    modelName: string;

    /**
     * Can be the model name if the model name is easy to understand. Otherwise it should describe the sensor.
     * It should be evident what kind of sensor it is, and what variant it is if there are multiple that Verdi
     * supports.
     * It should NOT include the manufacturer name
     */
    friendlyName: string;
    /**
     * Can be the same as the friendly name if the friendly name is short. Otherwise, it should communicate as much
     * info as possible about the sensor so it can be distinguished from others, while remaining under a soft 15 character
     * limit.
     */
    friendlyShortName: string;

    /**
     * Defines what phenomenon this sensor measures. Liquid flow, air flow, air temperature, etc.
     */
    unitCategory: UnitCategory;

    /**
     * The key for the icon that should be used to represent this sensor. If left blank, then the default for the unit
     * category will be used instead. Acceptable keys are, as of this writing, not defined at all.
     */
    iconKey?: string;

    /**
     * Set to true if it is impossible to disconnect the sensor from the product.
     */
    isOnboard: boolean;

    /**
     * What type of communication protocol is used to communicate with this sensor
     */
    communicationProtocol?: "ADC" | "PWM" | "I2C" | "SDI-12" | "RS-485" | "RS-232";

    /**
     * Deprecating this with sapling
     */
    saplingConversion?: (arg: number) => number;

    /**
     * Deprecating this in next iteration of this format
     * When automation mode is improved or made more clear, this can go
     */
    openValveIncreasesDatabaseValue: boolean;
};

/**
 * The `SensorConfiguration` type is the standard type for all downstream uses.
 *
 * - `SensorConfigurationBase` is the type used to define individual sensor configurations.
 * - Each `SensorConfigurationBase` instance is a value within the "SensorConfigurationsNoExplicitKeys" object.
 * - These instances are then converted into `SensorConfiguration` objects, which are added as values to the "SensorConfigurations" object.
 *
 * `SensorConfigurations` are generated automatically from instances of `SensorConfigurationBase` by adding
 * the `key` field, ensuring that the key is only written once.
 */
export type SensorConfiguration = SensorConfigurationBase & { key: SensorType };

/**
 * The type for the actual sensor params based on a SensorParameterConfig.
 * Numeric literals are widened to `number` and string literals to `string` so that
 * runtime values (which are never literal types) satisfy this type without casts.
 */
export type InferredSensorParams<T extends Record<string, SensorParameterConfig>> = {
    [K in keyof T]: T[K] extends SensorParameterConfigBase<infer V>
        ? V extends number
            ? number
            : V extends string
              ? string
              : never
        : never;
};

/**
 * Represents units for different physical phenomena measured by sensors.
 * Sensors in the same unit category must:
 * - measure the same physical phenomenon
 * - use the same canonical/base unit in our system
 *
 * For example, `airTemperature` and `plantTemperature` are distinct unit categories even though
 * their shared canonical unit in our system is Celsius.
 */
export type UnitCategory =
    | "liquidFlow"
    | "humidity"
    | "airTemperature"
    /** Temperature of the device's own circuit board, not of the surrounding air. */
    | "boardTemperature"
    | "plantTemperature"
    | "soilTemperature"
    | "liquidPressure"
    | "volumetricSoilMoisture"
    | "resistiveSoilMoisture"
    | "soilTension"
    | "soilElectricalConductivity" // should be named to bulkSoilElectricalConductivity at a future date
    | "poreSoilElectricalConductivity"
    | "relativePermittivity"
    | "windSpeed"
    /**
     * The peak wind speed over a reporting interval, as against `windSpeed`, which is the mean over
     * that same interval. A separate category despite sharing units and a conversion with it, because
     * an interval high aggregates differently from an interval mean — see `preferredBucketAggregate`
     * on `UnitCategoryConfiguration`.
     */
    | "windGust"
    | "windHeading"
    | "precipitation"
    | "liquidTankLevel"
    | "sapFlow"
    | "stemWaterPotential"
    | "electricalVoltage"
    | "evapotranspiration"
    | "electricalPulseCount"
    | "soilVolumetricIonContent"
    | "flowmeterKFactor"
    | "flowmeterOffset"
    | "flowmeterMinimumFrequency"
    | "pressureTransducerSlope"
    | "liquidTankLevelSlope"
    | "dimensionless"
    | "arbitraryPercentage"
    /**
     * Whether a device output is energized — see `OutputPosition`.
     *
     * Categorical like `outputOperatingMode` below, so the only allowed unit is `dimensionless`.
     */
    | "outputPosition"
    /**
     * The operating mode of a device output — see `OutputOperatingMode`.
     *
     * This is the first *categorical* unit category: the stored value is an enum member identifying a
     * mode, not a quantity. It therefore must never be averaged, interpolated, or unit-converted, and
     * the only allowed unit is `dimensionless`.
     *
     * Deliberately bespoke rather than a general categorical-stream mechanism — see `solenoidVerification`
     * below for a second instance of the same bespoke pattern. Other categorical series (last
     * error code) are still smuggled through the `dimensionless` category via the `code` element kind,
     * which loses that distinction. Generalizing is worth revisiting once there are several more
     * instances to generalize from.
     */
    | "outputOperatingMode"
    /**
     * The result of a Gen 4 solenoid actuation check — see `SolenoidVerificationResult`.
     *
     * Categorical, following the same bespoke pattern as `outputOperatingMode` above.
     */
    | "solenoidVerification"
    | "ndvi"
    /**
     * The statistical variance of an NDVI measurement — i.e. the expected value of the squared
     * deviation from the mean NDVI. Because NDVI is dimensionless, its variance is also dimensionless.
     */
    | "ndviVariance"
    | "stemRadiusChange"
    | "solarRadiation"
    | "depth"
    | "barometricPressure"
    /** UV Index, e.g. as reported by a weather station — dimensionless, not to be unit-converted. */
    | "uvIndex"
    /** Row, plant, and emitter spacing within a zone. */
    | "inFieldSpacing"
    | "rowLength"
    | "emitterFlowRate"
    /** Water applied by irrigation over a period, derived from flow; no sensor reports it. */
    | "totalIrrigationVolume";
